import { PropertyValueMap, TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property, state } from 'lit/decorators.js';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import { BlocksAccordionPanel } from '../accordion-panel/accordion-panel';
import type { AccordionSize } from './accordion.model';
import styles from './accordion.styles';

/**
 * @slot default- The component content
 * @csspart base - The component's base wrapper.
 * @csspart part-default - The component's default slot.
 */

export class BlocksAccordion extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * expandAll - All panel will be in expand or open state when it's true
   */
  @property({ type: Boolean })
  expandAll: boolean | undefined;

  /**
   * size - Accordion header size will be changed based on this property
   */
  @property({ type: String })
  size: AccordionSize;

  /**
   * Inverted variant
   * 1) Used for dark backgrounds
   */
  @property({ type: Boolean })
  inverted: boolean | undefined;

  /**
   * Display only a single panel at a time
   */
  @property({ type: Boolean })
  singlePanelOnly = false;

  /**
   * Active Accordion item state
   */
  @state()
  private activeAccordion: BlocksAccordionPanel | undefined | null;

  /**
   * Query all the accordion panels in the accordion
   */
  private get _catAccordionPanels(): Array<BlocksAccordionPanel> {
    return [...Array.from(this.querySelectorAll<BlocksAccordionPanel>('cat-accordion-panel'))];
  }

  /**
   * Handle exapandAll, variants, adding index to the panel and singlePanelOnly updates.
   * 1) Add index to each panel level
   */
  private setExpandAll() {
    this._catAccordionPanels.forEach((panel, index) => {
      panel.size = this.size;
      panel.index = index; /* 1 */
      if (this.expandAll) {
        if (!panel.disabled) {
          panel.isActive = true;
        }
      } else {
        panel.isActive = false;
      }
    });
  }

  /**
   * Updated lifecycle
   */
  updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>) {
    if (changedProperties.has('expandAll') || changedProperties.has('size')) {
      this.setExpandAll();
    }
  }

  /**
   * Set single panel only
   * 1) Close all panels except for the one that was clicked
   */
  private setSinglePanelOnly = (event: Event) => {
    const panel = (event.target as HTMLElement).closest('cat-accordion-panel');
    if (!panel?.disabled) {
      const accordionPanels = this.shadowRoot?.querySelector('slot')?.assignedElements() as BlocksAccordionPanel[];
      const notActive = accordionPanels.filter((el: BlocksAccordionPanel) => el !== panel);
      notActive.forEach((panel: BlocksAccordionPanel) => {
        panel.isActive = false;
        panel.height = '0';
      });
    }
  };

  /**
   * Opens all enabled accordion-panel in an accordion
   */
  public openAll() {
    this._catAccordionPanels.forEach((panel) => {
      if (!panel.disabled) {
        panel.open();
      }
    });
  }

  /**
   * Close all enabled accordion-panel in an accordion
   */
  public closeAll() {
    this._catAccordionPanels.forEach((panel) => {
      if (!panel.disabled) {
        panel.close();
      }
    });
  }

  /**
   * Keydown function for HOME and END key in an accordion
   */
  private handleKeydown(event: KeyboardEvent): void {
    const accordionPanelLength = this._catAccordionPanels.length - 1;
    let focusIndex = 0;
    if (KEYS.HOME === event.key || KEYS.END === event.key) {
      event.preventDefault();
      focusIndex = KEYS.HOME === event.key ? 0 : accordionPanelLength;
      this.activeAccordion = this._catAccordionPanels[focusIndex]?.shadowRoot?.querySelector('.cat-c-accordion-panel__button');
      this.activeAccordion?.focus();
    }
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-accordion', {
      'cat-c-accordion--single-panel-only': this.singlePanelOnly,
      'cat-c-accordion--inverted': this.inverted === true
    });

    return html`
      <div part="base" class="${componentClassNames}">
        <slot
          part="part-default"
          @click=${this.singlePanelOnly && !this.expandAll ? this.setSinglePanelOnly : nothing}
          @keydown=${this.handleKeydown}
        ></slot>
      </div>
    `;
  }
}

if (customElements.get('cat-accordion') === undefined) {
  customElements.define('cat-accordion', BlocksAccordion);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-accordion': BlocksAccordion;
  }
}
